import {
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Button,
  Portal,
  Text,
  UseDisclosureProps,
  Tooltip,
  Box,
  Divider,
} from '@chakra-ui/react';
import { BarChart04 } from '@kula-ai/icons/outline/BarChart04';
import { ChevronRight } from '@kula-ai/icons/outline/ChevronRight';
import { Maximize01 } from '@kula-ai/icons/outline/Maximize01';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { Check } from '@kula-ai/icons/outline/Check';
import { Download01 } from '@kula-ai/icons/outline/Download01';
import React, { useState, useCallback, useEffect } from 'react';
import { useDownloadReports, useShowToast } from '@/hooks';
import { CenterSpinner } from '@/components/ats';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_SERVICE } from '../../api/api';
import { reportChartConfigs } from '../ReportCharts/reportChartConfig';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadReports } from '../../show/headerActions/DownloadReports';
import { useKFlags } from '@/hooks/use-k-flags';
import { KText } from '@/ui-library/components';
import { Radio } from '@kula-ai/components';
import { usePermission } from '@/ui/permission';
import { DotsVertical } from '@kula-ai/icons-core';

interface ReportTileActionsProps {
  tile: Record<string, unknown>;
  report?: Record<string, unknown>;
  enableChartSwitcher?: boolean;
  onFullScreen?: () => void;
  className?: string;
  transformedData?: unknown;
  tileActionMenuDisclosure?: UseDisclosureProps;
  isStaticReport?: boolean;
  customStageMapping?: Record<string, unknown>;
  viewMode?: boolean;
  isEditMode?: boolean;
  onRemoveBlock?: (_blockId: string) => void;
  onTileMetaChange?: (_tileId: string, _metadata: unknown) => void;
  uniqueTileId?: string;
}

const isEmpty = (data: unknown[], field: string | string[] = 'y') => {
  if (!data?.length) return true;

  if (!field) {
    return !data?.some((d) => d);
  }
  if (Array.isArray(field)) {
    return !data?.some((d) =>
      field.some((f) => (d as Record<string, unknown>)[f])
    );
  }
  return !data?.some((d) => (d as Record<string, unknown>)[field]);
};

// Helper component for chart menu items with proper parent menu closing
const ChartMenuItem: React.FC<{
  type: string;
  chartLabel: string;
  isSelected: boolean;
  isEditMode: boolean;
  onChartTypeChange: (_chartType: string) => void;
  onSubMenuClose: () => void;
  onParentMenuClose: () => void;
}> = ({
  type,
  chartLabel,
  isSelected,
  isEditMode,
  onChartTypeChange,
  onSubMenuClose,
  onParentMenuClose,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Perform the chart type change
    onChartTypeChange(type);

    // Close submenu first
    onSubMenuClose();

    // Close parent menu with a small delay to ensure state is updated
    setTimeout(() => {
      onParentMenuClose();
    }, 10);
  };

  if (isEditMode) {
    return (
      <MenuItem onClick={handleClick} py={3}>
        <Flex alignItems="center" width="100%">
          <Radio
            label={chartLabel}
            checked={isSelected}
            size="sm"
            labelClassName="text-sm font-medium text-secondary-900"
          />
        </Flex>
      </MenuItem>
    );
  }

  return (
    <MenuItem
      onClick={handleClick}
      bg={isSelected ? 'ats-tertiary' : 'transparent'}
      _hover={{
        bg: isSelected ? 'ats-tertiary' : 'gray.50',
      }}
      borderRadius="4px"
      p="8px 12px"
    >
      <Flex gap="8px" alignItems="center" justify="space-between" width="100%">
        <Text size="sm-medium" color={isSelected ? 'ats-primary' : 'inherit'}>
          {chartLabel}
        </Text>
        {isSelected && (
          <Icon as={Check} width="16px" height="16px" color="ats-primary" />
        )}
      </Flex>
    </MenuItem>
  );
};

export const ReportTileActions: React.FC<ReportTileActionsProps> = ({
  tile,
  report,
  enableChartSwitcher = false,
  onFullScreen,
  className: _className,
  transformedData,
  tileActionMenuDisclosure: _tileActionMenuDisclosure,
  isStaticReport = false,
  customStageMapping = {},
  viewMode = false,
  isEditMode = false,
  onRemoveBlock,
  onTileMetaChange,
  uniqueTileId,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const deleteDisclosure = useDisclosure();
  const mainMenuDisclosure = useDisclosure();
  const subMenuDisclosure = useDisclosure();
  const { handleDownloadCSV, isPolling } = useDownloadReports(report, tile);
  const { feReportsV3 } = useKFlags();

  const metadata = (tile?.metadata ?? {}) as {
    componentType?: string;
    tileKey?: string;
  };
  const { componentType, tileKey } = metadata;
  const tileKeyStr = String(tileKey ?? '');
  const dispatch = useDispatch();

  const { defaultChart, alternatives = [] } =
    reportChartConfigs[tileKeyStr] || {};

  const currentChartType = useSelector(
    (state: {
      staticReports: { chartTypes: Record<string, string> };
      customReports: { chartTypes: Record<string, string> };
    }) => {
      //  chart keys for static vs custom reports
      const chartKeyForState = isStaticReport
        ? `static_${tileKeyStr}`
        : (uniqueTileId ?? tileKeyStr);
      return (
        (isStaticReport
          ? state.staticReports.chartTypes[chartKeyForState]
          : state.customReports.chartTypes[chartKeyForState]) || defaultChart
      );
    }
  );

  // Handle chart type change
  const handleChartTypeChange = useCallback(
    (type: string) => {
      // chart keys for static vs custom reports
      const chartKeyForState = isStaticReport
        ? `static_${tileKeyStr}`
        : (uniqueTileId ?? tileKeyStr);
      onTileMetaChange?.(tile?.id as string, { defaultChart: type });
      dispatch({
        type: isStaticReport
          ? 'staticReports/setChartType'
          : 'customReports/setChartType',
        payload: { chartKey: chartKeyForState, chartType: type },
      });
    },
    [
      dispatch,
      tileKeyStr,
      isStaticReport,
      tile?.id,
      onTileMetaChange,
      uniqueTileId,
    ]
  );

  const toast = useShowToast();
  const queryClient = useQueryClient();

  // Close menus on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (subMenuDisclosure.isOpen) {
        subMenuDisclosure.onClose();
      }
      if (mainMenuDisclosure.isOpen) {
        mainMenuDisclosure.onClose();
      }
    };

    // Add scroll listener to window and any scrollable containers
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [subMenuDisclosure, mainMenuDisclosure]);
  const uniqueId = `${tile.subject_type}-${tile.id}`;

  const deleteTilesMutation = useMutation({
    mutationFn: (data: { reportID: string; payload: unknown }) =>
      API_SERVICE.deleteTiles(data.reportID, data.payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`getCustomReports/${report?.id}`],
      });
      deleteDisclosure.onClose();
      toast({ title: 'Block deleted successfully' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to delete the block',
        description: error.message,
        status: 'error',
      });
    },
  });

  const _handleTileDownload = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDownloading(true);

      try {
        const element = document.getElementById(uniqueId);
        if (!element) {
          throw new Error('Report content not found');
        }

        const clonedElement = element.cloneNode(true) as HTMLElement;
        // Remove the actions box from the cloned content for PDF
        const actionsBox = clonedElement.querySelector('.tile-actions');
        if (actionsBox) {
          actionsBox.remove();
        }

        // Remove the checkbox if it exists
        const checkbox = clonedElement.querySelector('.checkbox-field');
        if (checkbox) {
          checkbox.remove();
        }

        Object.assign(clonedElement.style, {
          backgroundColor: '#ffffff',
          padding: '20px',
          width: '100%',
        });
      } catch (error) {
        setIsDownloading(false);
        toast({
          title: 'Download failed',
          description:
            error instanceof Error ? error.message : 'Unknown error occurred',
          status: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uniqueId, tile.name, toast]
  );

  const dataRows = (
    Array.isArray(transformedData) ? transformedData : []
  ) as Record<string, unknown>[];

  const handleDelete = useCallback(() => {
    if (feReportsV3 && onRemoveBlock) {
      onRemoveBlock(String(tile.id));
      deleteDisclosure.onClose();
    } else {
      // Use original API approach for existing functionality
      deleteTilesMutation.mutate({
        reportID: String(report?.id ?? ''),
        payload: { ids: [tile.id] },
      });
    }
  }, [
    report,
    tile,
    deleteTilesMutation,
    feReportsV3,
    onRemoveBlock,
    deleteDisclosure,
  ]);

  const downloadPermission1 = usePermission({
    permission: 'api_ats_v1_reports_saved_report.download_reports?',
  });
  const downloadPermission2 = usePermission({
    permission: 'api_ats_v1_reports_saved_report.generate?',
  });

  const hasDownloadPermission = downloadPermission1 && downloadPermission2;

  if (isDownloading) {
    return <CenterSpinner />;
  }

  // All metric fields to check for recruiter performance (excluding _total fields and metadata fields)
  const metricFields = [
    'sourced_candidates',
    'screened_candidates',
    'interviews_scheduled',
    'interviews_conducted',
    'interviews_rescheduled',
    'offers_sent',
    'offers_accepted',
    'time_to_offer',
    'offer_revisions_done',
    'hired_candidates',
    'time_to_hire',
    'rejected_candidates',
  ];

  const getRecruiterPerformanceField = (): string => {
    if (!dataRows.length) {
      return 'sourced_candidates';
    }

    for (const field of metricFields) {
      if (dataRows.some((row) => Number(row[field]) > 0)) {
        return field;
      }
    }

    return 'sourced_candidates';
  };

  const getFieldSurveyWithData = (): string => {
    if (!dataRows.length) {
      return 'candidate_nps';
    }

    const npsFields = ['candidate_nps', 'hm_nps'];
    for (const field of npsFields) {
      if (dataRows.some((row) => Number(row[field]) > 0)) {
        return field;
      }
    }

    return 'candidate_nps';
  };

  const getFlowHealthFieldWithData = (): string => {
    if (!dataRows.length) {
      return 'contacted';
    }

    const flowHealthFields = ['contacted', 'opened', 'replied', 'interested'];
    for (const field of flowHealthFields) {
      if (
        dataRows.some(
          (row) => Number((row as Record<string, unknown>)[field]) > 0
        )
      ) {
        return field;
      }
    }

    return 'contacted';
  };

  const getFlowUserActivityFieldWithData = (): string => {
    if (!dataRows.length) {
      return 'added';
    }

    const userActivityFields = [
      'added',
      'sent_first',
      'sent_followup',
      'replied',
      'interested',
    ];
    for (const field of userActivityFields) {
      if (
        dataRows.some(
          (row) => Number((row as Record<string, unknown>)[field]) > 0
        )
      ) {
        return field;
      }
    }

    return 'added';
  };

  const field = (tile: Record<string, unknown>) => {
    switch (tile.subject_type) {
      case 'interviews':
        return 'applicants_interviewed';
      case 'offers':
        return 'offers';
      case 'hires':
        return 'hires';
      case 'contacts':
        return 'contacts_created';
      case 'jobs':
        return 'opened_jobs';
      case 'rejections':
        return 'rejections';
      case 'pipeline':
        return Object.values(customStageMapping)?.length
          ? 's1_total_count'
          : 'application_review_total_count';
      case 'live_job_pipeline': {
        const stageCount = Object.keys(customStageMapping || {}).length;
        return stageCount
          ? Array.from(
              { length: stageCount },
              (_, i) => `s${i + 1}_actual_count`
            )
          : ['prospect_total_count', 'application_review_total_count'];
      }
      case 'flow_health':
        return getFlowHealthFieldWithData();
      case 'flow_user_activity':
        return getFlowUserActivityFieldWithData();
      case 'recruiter_performance':
        return getRecruiterPerformanceField();
      case 'requisitions':
        return 'requisitions_opened';
      case 'ats_survey':
        return getFieldSurveyWithData();
      default:
        return 'applicants_interviewed';
    }
  };

  const renderMenu = () => {
    // Check if any menu items would be displayed
    const hasChartSwitcher = enableChartSwitcher;
    const hasDownloadMenu =
      hasDownloadPermission &&
      componentType === 'table' &&
      tile?.subject_type !== 'eeoc' &&
      !isEditMode;
    const hasExpandOption = componentType !== 'card' && !isEditMode;
    const hasDeleteOption = feReportsV3
      ? isEditMode
      : !isStaticReport && !viewMode;

    const hasMenuItems =
      hasChartSwitcher || hasDownloadMenu || hasExpandOption || hasDeleteOption;

    const viewModeCard = componentType === 'card' && viewMode;

    // Don't render menu if no items would be displayed
    if (!hasMenuItems || viewModeCard) return null;

    return (
      <Menu
        isLazy
        placement="bottom-end"
        autoSelect={false}
        closeOnBlur={true}
        closeOnSelect={true}
        isOpen={mainMenuDisclosure.isOpen}
        onClose={() => {
          mainMenuDisclosure.onClose();
          subMenuDisclosure.onClose();
        }}
      >
        <MenuButton
          paddingEnd="none"
          paddingStart="none"
          p="7.5px"
          border="1px solid #EFECF0"
          bg="white"
          borderRadius="6px"
          _hover={{
            bg: 'white',
          }}
          onClick={() => {
            mainMenuDisclosure.onOpen();
          }}
        >
          <DotsVertical size={16} />
        </MenuButton>
        <Portal>
          <MenuList zIndex="9999" minWidth="156px" w="180px">
            {enableChartSwitcher && (
              <Menu
                placement="right-start"
                autoSelect={false}
                isOpen={subMenuDisclosure.isOpen}
                closeOnBlur={true}
                closeOnSelect={true}
                strategy="fixed"
                onClose={() => {
                  subMenuDisclosure.onClose();
                }}
              >
                <MenuButton
                  as={Button}
                  p="8px 12px"
                  borderRadius="4px"
                  _hover={{
                    bg: 'gray.50',
                  }}
                  variant="unstyled"
                  width="100%"
                  height="auto"
                  textAlign="left"
                  onClick={() => {
                    subMenuDisclosure.onOpen();
                    // setIsDownloadMenuOpen(false);
                  }}
                >
                  <Flex
                    gap="8px"
                    alignItems="center"
                    justify="space-between"
                    width="100%"
                  >
                    <Flex gap="8px" alignItems="center">
                      <Icon as={BarChart04} width="16px" height="16px" />
                      <Text size="sm-medium">Charts</Text>
                    </Flex>
                    <Icon as={ChevronRight} width="16px" height="16px" />
                  </Flex>
                </MenuButton>
                <Portal>
                  <MenuList
                    minW="120px"
                    className="submenu-list"
                    zIndex="10000"
                    p={isEditMode ? '0' : '8px'}
                  >
                    {isEditMode && (
                      <Box
                        p="12px"
                        borderBottomWidth="1px"
                        borderColor="border-color"
                        borderTopRadius="8px"
                        bg="secondary-25"
                        position="sticky"
                        top={0}
                        zIndex={1}
                      >
                        <KText color="secondary-900" size="sm-semi-bold">
                          Default chart type
                        </KText>
                      </Box>
                    )}
                    {[defaultChart, ...alternatives]?.map((type, index) => {
                      const isSelected = currentChartType === type;
                      const chartLabel =
                        type === 'pyramid'
                          ? 'Funnel'
                          : type === 'horizontalStackedBar'
                            ? 'Pipeline bar'
                            : type?.charAt(0).toUpperCase() + type?.slice(1);

                      if (isEditMode) {
                        return (
                          <Box key={type}>
                            <ChartMenuItem
                              type={type}
                              chartLabel={chartLabel}
                              isSelected={isSelected}
                              isEditMode={isEditMode}
                              onChartTypeChange={handleChartTypeChange}
                              onSubMenuClose={subMenuDisclosure.onClose}
                              onParentMenuClose={mainMenuDisclosure.onClose}
                            />
                            {index <
                              [defaultChart, ...alternatives].length - 1 && (
                              <Divider
                                borderStyle="solid"
                                borderColor="border-color"
                              />
                            )}
                          </Box>
                        );
                      }

                      return (
                        <ChartMenuItem
                          key={type}
                          type={type}
                          chartLabel={chartLabel}
                          isSelected={isSelected}
                          isEditMode={isEditMode}
                          onChartTypeChange={handleChartTypeChange}
                          onSubMenuClose={subMenuDisclosure.onClose}
                          onParentMenuClose={mainMenuDisclosure.onClose}
                        />
                      );
                    })}
                  </MenuList>
                </Portal>
              </Menu>
            )}

            {hasDownloadMenu && (
              <Tooltip
                label="No data available to download"
                placement="top"
                hasArrow
                isDisabled={!isEmpty(dataRows as unknown[], field(tile))}
              >
                <MenuItem
                  onClick={() => {
                    if (isEmpty(dataRows as unknown[], field(tile))) {
                      return;
                    }
                    handleDownloadCSV();
                  }}
                  opacity={
                    isEmpty(dataRows as unknown[], field(tile)) ? 0.5 : 1
                  }
                  disabled={isEmpty(dataRows as unknown[], field(tile))}
                  _disabled={{
                    bg: 'secondary-100 !important',
                    cursor: 'not-allowed !important',
                  }}
                  _hover={{
                    bg: isEmpty(dataRows as unknown[], field(tile))
                      ? 'secondary-100'
                      : 'secondary-50',
                  }}
                  cursor={
                    isEmpty(dataRows as unknown[], field(tile))
                      ? 'not-allowed'
                      : 'pointer'
                  }
                >
                  <Flex gap="8px" alignItems="center">
                    <Icon as={Download01} width="16px" height="16px" />
                    <Text size="sm-medium">Download CSV</Text>
                  </Flex>
                </MenuItem>
              </Tooltip>
            )}

            {hasExpandOption && (
              <MenuItem onClick={onFullScreen}>
                <Flex gap="8px" alignItems="center">
                  <Icon as={Maximize01} width="16px" height="16px" />
                  <Text size="sm-medium">Expand</Text>
                </Flex>
              </MenuItem>
            )}

            {hasDeleteOption && (
              <MenuItem onClick={handleDelete}>
                <Flex gap="8px" alignItems="center">
                  <Icon as={Trash01} width="16px" height="16px" />
                  <Text size="sm-medium">Delete</Text>
                </Flex>
              </MenuItem>
            )}
          </MenuList>
        </Portal>
      </Menu>
    );
  };

  return (
    <>
      {renderMenu()}
      <DownloadReports
        isPolling={isPolling}
        pollingText="CSV is being prepared"
      />
    </>
  );
};
